import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface Args {
	/** Ids of the records the table lists, in listed order. */
	ids: string[];
	currentId: string | undefined;
	getPath: (id: string) => string;
}

/**
 * Prev/next navigation across the records of the originating table. The table keeps its
 * filters, sorting and paging in the URL search params, so carrying `search` across keeps the
 * table's state for the way back.
 */
export const useDetailsPagination = ({ ids, currentId, getPath }: Args) => {
	const navigate = useNavigate();
	const { search } = useLocation();

	const currentIndex = useMemo(() => ids.findIndex((id) => id === currentId), [ids, currentId]);

	const total = ids.length;
	const prevDisabled = currentIndex <= 0;
	const nextDisabled = currentIndex < 0 || currentIndex >= total - 1;

	const goToOffset = useCallback(
		(offset: number) => {
			const targetId = ids[currentIndex + offset];

			if (!targetId) {
				return;
			}

			navigate({ pathname: getPath(targetId), search });
		},
		[ids, currentIndex, getPath, navigate, search]
	);

	const goPrev = useCallback(() => {
		if (!prevDisabled) {
			goToOffset(-1);
		}
	}, [prevDisabled, goToOffset]);

	const goNext = useCallback(() => {
		if (!nextDisabled) {
			goToOffset(1);
		}
	}, [nextDisabled, goToOffset]);

	return {
		currentIndex,
		total,
		prevDisabled,
		nextDisabled,
		goPrev,
		goNext,
	};
};
